import { useState, useEffect, useRef } from 'react'
import { GameState, Item } from '../store/types'
import { apiShopList, apiShopBuy, apiShopSell } from '../server/ServiceCall'

const GRADE_COLOR: Record<string, string> = {
  COMMON:'#a0a0a0', UNCOMMON:'#60c060', RARE:'#4090e0',
  EPIC:'#a060e0', LEGEND:'#e0a030', MYTH:'#e04060',
}
const GRADE_KO: Record<string, string> = {
  COMMON:'일반', UNCOMMON:'고급', RARE:'희귀', EPIC:'영웅', LEGEND:'전설', MYTH:'신화',
}
const CATEGORY_KO: Record<string, string> = {
  EQUIP:'장비', CONSUME:'소모품', MUGONG_BOOK:'비급', MATERIAL:'재료', HERB:'약초',
}
const SLOT_LABELS: Record<string, string> = {
  WEAPON:'무기', CLOTH:'도복', HAT:'모자', GLOVE:'장갑',
  SHOE:'신발', CLOAK:'쾌자', INNER:'속적삼', NECK:'목걸이', BRACELET:'팔찌',
}
const CAT_ORDER: Record<string, number> = {
  EQUIP:0, MUGONG_BOOK:1, CONSUME:2, HERB:3, MATERIAL:4,
}
const SLOT_ORDER: Record<string, number> = {
  WEAPON:0, CLOTH:1, HAT:2, INNER:3, CLOAK:4, GLOVE:5, SHOE:6, NECK:7, BRACELET:8,
}
const GRADE_ORDER: Record<string, number> = {
  MYTH:0, LEGEND:1, EPIC:2, RARE:3, UNCOMMON:4, COMMON:5,
}
function itemSortKey(cat: string, slot: string | null | undefined, grade: string) {
  return [(CAT_ORDER[cat] ?? 9), (SLOT_ORDER[slot ?? ''] ?? 9), (GRADE_ORDER[grade] ?? 9)]
}
function cmpSort(a: number[], b: number[]) {
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return a[i] - b[i]
  return 0
}

interface ShopItem {
  shopId: string; shopName: string; regionId: string | null
  itemId: string; itemName: string; category: string; slot: string | null
  grade: string; atkBonus: number; defBonus: number; hpBonus: number; speedBonus: number
  buyPrice: number; sellPrice: number; description: string | null
  isRandom: boolean; stock: number | null; expireAt: string | null
}

interface Tooltip { item: ShopItem | Item; x: number; y: number }

interface Props { gs: GameState; token: string; onRefresh: () => void }

function formatExpire(expireAt: string | null): string {
  if (!expireAt) return ''
  const diff = Math.max(0, new Date(expireAt).getTime() - Date.now())
  const m = Math.floor(diff / 60000)
  const s = Math.floor((diff % 60000) / 1000)
  if (m >= 60) return `${Math.floor(m / 60)}시간 후 갱신`
  if (m > 0) return `${m}분 ${s}초 후 갱신`
  return `${s}초 후 갱신`
}

export default function TabShop({ gs, token, onRefresh }: Props) {
  const [items,    setItems]    = useState<ShopItem[]>([])
  const [loading,  setLoading]  = useState(false)
  const [mode,     setMode]     = useState<'buy'|'sell'>('buy')
  const [shopId,   setShopId]   = useState<string>('')
  const [selected, setSelected] = useState<ShopItem | Item | null>(null)
  const [qty,      setQty]      = useState(1)
  const [busy,     setBusy]     = useState(false)
  const [msg,      setMsg]      = useState('')
  const [msgOk,    setMsgOk]    = useState(false)
  const [tooltip,  setTooltip]  = useState<Tooltip | null>(null)
  const [tick,     setTick]     = useState(0)
  const [catTab,   setCatTab]   = useState<string>('전체')
  const [slotTab,  setSlotTab]  = useState<string>('전체')

  // 갱신 카운트다운 타이머
  useEffect(() => {
    const t = setInterval(() => setTick(v => v + 1), 10000)
    return () => clearInterval(t)
  }, [])

  function showMsg(m: string, ok = false) { setMsg(m); setMsgOk(ok); setTimeout(() => setMsg(''), 3000) }

  useEffect(() => {
    setShopId('')
    setSelected(null)
    setLoading(true)
    apiShopList(token, gs.char.currentRegion)
      .then(res => {
        if (res.resultCode === '00') {
          const raw = JSON.parse(res.resultData || '[]') as any[]
          const mapped = raw.map(r => ({
            shopId: r.SHOP_ID, shopName: r.SHOP_NAME, regionId: r.REGION_ID,
            itemId: r.ITEM_ID, itemName: r.ITEM_NAME, category: r.CATEGORY, slot: r.SLOT,
            grade: r.GRADE, atkBonus: Number(r.ATK_BONUS)||0, defBonus: Number(r.DEF_BONUS)||0,
            hpBonus: Number(r.HP_BONUS)||0, speedBonus: Number(r.SPEED_BONUS)||0,
            buyPrice: Number(r.BUY_PRICE)||0, sellPrice: Number(r.SELL_PRICE)||0,
            description: r.DESCRIPTION ?? null,
            isRandom: r.IS_RANDOM === 1,
            stock: r.STOCK != null ? Number(r.STOCK) : null,
            expireAt: r.EXPIRE_AT ?? null,
          }))
          setItems(mapped)
          if (mapped.length > 0) setShopId(mapped[0].shopId)
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [gs.char.currentRegion])

  const shops = Array.from(new Map(items.map(i => [i.shopId, { id: i.shopId, name: i.shopName }])).values())
  const allShopItems = items
    .filter(i => i.shopId === shopId)
    .sort((a, b) => cmpSort(itemSortKey(a.category, a.slot, a.grade), itemSortKey(b.category, b.slot, b.grade)))

  // 카테고리 탭 목록 (존재하는 것만)
  const presentCats = ['전체', ...Object.keys(CAT_ORDER)
    .filter(c => allShopItems.some(i => i.category === c))]

  // 카테고리 필터
  const catFiltered = catTab === '전체' ? allShopItems : allShopItems.filter(i => i.category === catTab)

  // 장비 슬롯 서브탭
  const presentSlots = catTab === 'EQUIP'
    ? ['전체', ...Object.keys(SLOT_ORDER).filter(s => catFiltered.some(i => i.slot === s))]
    : []
  const shopItems = catTab === 'EQUIP' && slotTab !== '전체'
    ? catFiltered.filter(i => i.slot === slotTab)
    : catFiltered
  const sellable = gs.inventory
    .filter(item => !item.equipSlot)
    .sort((a, b) => cmpSort(itemSortKey(a.category, a.slot, a.grade), itemSortKey(b.category, b.slot, b.grade)))

  function handleMouseEnter(e: React.MouseEvent, item: ShopItem | Item) {
    setTooltip({ item, x: e.clientX, y: e.clientY })
  }
  function handleMouseMove(e: React.MouseEvent) {
    if (tooltip) setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)
  }
  function handleMouseLeave() { setTooltip(null) }

  async function doBuy() {
    const item = selected as ShopItem
    if (!item || !shopId) return
    setBusy(true)
    try {
      const res = await apiShopBuy(token, shopId, item.itemId, qty)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') { setSelected(null); onRefresh() }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doSell() {
    const item = selected as Item
    if (!item) return
    setBusy(true)
    try {
      const res = await apiShopSell(token, item.invId, qty)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') { setSelected(null); onRefresh() }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  // 툴팁 컴포넌트
  function ItemTooltip() {
    if (!tooltip) return null
    const { item, x, y } = tooltip
    const isShopItem = 'itemId' in item
    const name   = isShopItem ? (item as ShopItem).itemName : (item as Item).name
    const grade  = item.grade
    const cat    = item.category
    const slot   = isShopItem ? (item as ShopItem).slot : (item as Item).slot
    const atk    = item.atkBonus; const def = item.defBonus
    const hp     = item.hpBonus;  const spd = item.speedBonus
    const desc   = isShopItem ? (item as ShopItem).description : null
    const hasStats = atk > 0 || def > 0 || hp > 0 || spd > 0

    const left = x + 170 > window.innerWidth ? x - 168 : x + 12
    const top  = Math.min(y - 10, window.innerHeight - 180)

    return (
      <div className="inven-tooltip" style={{ left, top, position:'fixed', zIndex:9999, pointerEvents:'none' }}>
        <div className="inven-tooltip-name" style={{ color: GRADE_COLOR[grade] }}>{name}</div>
        <div className="inven-tooltip-grade">{GRADE_KO[grade] ?? grade}</div>
        {cat && (
          <div className="inven-tooltip-cat">
            {CATEGORY_KO[cat] ?? cat}
            {slot && cat === 'EQUIP' && ` · ${SLOT_LABELS[slot] ?? slot}`}
          </div>
        )}
        {hasStats && (
          <div className="inven-tooltip-stats">
            {atk > 0 && <span>공격 +{atk}</span>}
            {def > 0 && <span>방어 +{def}</span>}
            {hp  > 0 && <span>체력 +{hp}</span>}
            {spd > 0 && <span>속도 +{spd}</span>}
          </div>
        )}
        {desc && <div style={{ fontSize:10, color:'#806050', marginTop:4, lineHeight:1.5 }}>{desc}</div>}
      </div>
    )
  }

  const selShopItem = selected && 'itemId' in selected ? selected as ShopItem : null
  const selInvItem  = selected && 'invId'  in selected ? selected as Item     : null

  return (
    <div className="tab-action" onMouseMove={handleMouseMove}>
      <ItemTooltip />
      {msg && <div className={`inven-msg${msgOk ? ' ok' : ''}`}>{msg}</div>}

      {/* 상점 선택 + 모드 전환 */}
      <div style={{ display:'flex', gap:6, marginBottom:4 }}>
        {shops.map(s => (
          <button key={s.id} className={`inven-view-btn${shopId===s.id?' active':''}`}
            style={{ fontSize:11, padding:'5px 8px' }}
            onClick={() => { setShopId(s.id); setSelected(null); setCatTab('전체'); setSlotTab('전체') }}>{s.name}</button>
        ))}
      </div>
      <div className="inven-view-tabs">
        <button className={`inven-view-btn${mode==='buy'?' active':''}`}
          onClick={() => { setMode('buy'); setSelected(null); setQty(1); setCatTab('전체'); setSlotTab('전체') }}>구매</button>
        <button className={`inven-view-btn${mode==='sell'?' active':''}`}
          onClick={() => { setMode('sell'); setSelected(null); setQty(1) }}>판매</button>
      </div>

      {/* 카테고리 탭 (구매 모드) */}
      {!loading && mode === 'buy' && (
        <>
          <div className="train-cat-tabs" style={{ marginTop:6 }}>
            {presentCats.map(cat => (
              <button key={cat}
                className={`train-cat-tab${catTab===cat?' active':''}`}
                onClick={() => { setCatTab(cat); setSlotTab('전체'); setSelected(null) }}
              >
                {cat === '전체' ? '전체' : (CATEGORY_KO[cat] ?? cat)}
              </button>
            ))}
          </div>
          {catTab === 'EQUIP' && presentSlots.length > 1 && (
            <div className="train-cat-tabs" style={{ marginTop:2 }}>
              {presentSlots.map(slot => (
                <button key={slot}
                  className={`train-cat-tab${slotTab===slot?' active':''}`}
                  onClick={() => { setSlotTab(slot); setSelected(null) }}
                >
                  {slot === '전체' ? '전체' : (SLOT_LABELS[slot] ?? slot)}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {loading ? (
        <div style={{ color:'#605040', textAlign:'center', padding:'30px 0' }}>불러오는 중...</div>
      ) : mode === 'buy' ? (
        <div className="bag-list">
          {shopItems.length === 0 && <div className="bag-empty">판매 중인 아이템이 없습니다.</div>}
          {shopItems.map(item => {
            const isEquip   = item.category === 'EQUIP'
            const isConsume = item.category === 'CONSUME' || item.category === 'HERB'
            const hasStats  = item.atkBonus > 0 || item.defBonus > 0 || item.hpBonus > 0 || item.speedBonus > 0
            const soldOut   = item.isRandom && item.stock === 0
            return (
              <div key={`${item.shopId}-${item.itemId}`}
                className={`bag-item${selShopItem?.itemId===item.itemId?' selected':''}${soldOut?' locked':''}`}
                onClick={() => !soldOut && (setSelected(selShopItem?.itemId===item.itemId ? null : item), setQty(1))}
                onMouseEnter={e => handleMouseEnter(e, item)}
                onMouseLeave={handleMouseLeave}
              >
                <span className="bag-grade-dot" style={{ background: GRADE_COLOR[item.grade] }} />
                <div style={{ display:'flex', flexDirection:'column', flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                    <span className="bag-item-name" style={{ color: item.isRandom ? GRADE_COLOR[item.grade] : undefined }}>
                      {item.itemName}
                    </span>
                    <span className="bag-item-cat">{CATEGORY_KO[item.category]??item.category}</span>
                    {item.isRandom && !soldOut && item.stock != null && item.stock <= 10 ? (
                      <span style={{ fontSize:9, background:'#3a1a1a', border:'1px solid #a04030',
                        color:'#e07050', borderRadius:2, padding:'0 3px', marginLeft:2 }}>잔여 {item.stock}개</span>
                    ) : item.isRandom && !soldOut && (
                      <span style={{ fontSize:9, background:'#3a1a40', border:'1px solid #6040a0',
                        color:'#c080e0', borderRadius:2, padding:'0 3px', marginLeft:2 }}>한정</span>
                    )}
                    {soldOut && (
                      <span style={{ fontSize:9, background:'#2a1a1a', border:'1px solid #604040',
                        color:'#806060', borderRadius:2, padding:'0 3px', marginLeft:2 }}>소진</span>
                    )}
                  </div>
                  {isEquip && hasStats && (
                    <div style={{ fontSize:10, color:'#8ab0c8', marginTop:1 }}>
                      {item.atkBonus>0     && <span style={{ marginRight:5 }}>공+{item.atkBonus}</span>}
                      {item.defBonus>0     && <span style={{ marginRight:5 }}>방+{item.defBonus}</span>}
                      {item.hpBonus>0      && <span style={{ marginRight:5 }}>생+{item.hpBonus}</span>}
                                            {item.speedBonus>0   && <span>속+{item.speedBonus}</span>}
                    </div>
                  )}
                  {isConsume && item.description && (
                    <div style={{ fontSize:10, color:'#80a870', marginTop:1 }}>{item.description}</div>
                  )}
                  {item.isRandom && item.expireAt && !soldOut && (
                    <div style={{ fontSize:9, color:'#706050', marginTop:1 }}>
                      {formatExpire(item.expireAt)}
                    </div>
                  )}
                </div>
                <span style={{ flexShrink:0, color: soldOut ? '#504030' : '#e0c040', fontSize:12, fontWeight:'bold', marginLeft:4 }}>
                  {soldOut ? '소진' : `${item.buyPrice.toLocaleString()}전`}
                </span>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="bag-list">
          {sellable.length === 0 && <div className="bag-empty">판매할 아이템이 없습니다.</div>}
          {sellable.map(item => {
            const hasStats = item.atkBonus>0||item.defBonus>0||item.hpBonus>0||item.speedBonus>0
            return (
              <div key={item.invId}
                className={`bag-item${selInvItem?.invId===item.invId?' selected':''}`}
                onClick={() => { setSelected(selInvItem?.invId===item.invId ? null : item); setQty(1) }}
                onMouseEnter={e => handleMouseEnter(e, item)}
                onMouseLeave={handleMouseLeave}
              >
                <span className="bag-grade-dot" style={{ background: GRADE_COLOR[item.grade] }} />
                <div style={{ display:'flex', flexDirection:'column', flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                    <span className="bag-item-name">{item.name}</span>
                    {item.qty > 1 && <span className="bag-item-qty">×{item.qty}</span>}
                  </div>
                  {item.category === 'EQUIP' && hasStats && (
                    <div style={{ fontSize:10, color:'#8ab0c8', marginTop:1 }}>
                      {item.atkBonus>0     && <span style={{ marginRight:5 }}>공+{item.atkBonus}</span>}
                      {item.defBonus>0     && <span style={{ marginRight:5 }}>방+{item.defBonus}</span>}
                      {item.hpBonus>0      && <span style={{ marginRight:5 }}>생+{item.hpBonus}</span>}
                                            {item.speedBonus>0   && <span>속+{item.speedBonus}</span>}
                    </div>
                  )}
                </div>
                <span style={{ flexShrink:0, color:'#e0c040', fontSize:11, marginLeft:4 }}>
                  {item.sellPrice > 0 ? `${item.sellPrice.toLocaleString()}전` : '판매'}
                </span>
              </div>
            )
          })}
        </div>
      )}

      {/* 구매 액션 패널 */}
      {selShopItem && mode === 'buy' && (
        <div className="item-action-panel">
          <div className="item-action-title" style={{ color: GRADE_COLOR[selShopItem.grade] }}>
            {selShopItem.itemName}
            {selShopItem.isRandom && selShopItem.stock != null && selShopItem.stock <= 10 ? (
              <span style={{ fontSize:10, color:'#e07050', marginLeft:6, fontWeight:'normal' }}>
                서버 잔여 {selShopItem.stock}개
              </span>
            ) : selShopItem.isRandom && (
              <span style={{ fontSize:10, color:'#c080e0', marginLeft:6, fontWeight:'normal' }}>
                한정
              </span>
            )}
          </div>
          <div className="item-action-info">
            {selShopItem.atkBonus>0     &&<span>공+{selShopItem.atkBonus}</span>}
            {selShopItem.defBonus>0     &&<span>방+{selShopItem.defBonus}</span>}
            {selShopItem.hpBonus>0      &&<span>생+{selShopItem.hpBonus}</span>}
                        {selShopItem.speedBonus>0   &&<span>속+{selShopItem.speedBonus}</span>}
            <span style={{ color:'#e0c040' }}>개당 {selShopItem.buyPrice.toLocaleString()}전</span>
            <span>보유 {gs.char.gold.toLocaleString()}전</span>
          </div>
          <div className="item-action-btns">
            {!['EQUIP','MUGONG_BOOK'].includes(selShopItem.category) && (
              <input type="number" className="drop-qty-input" min={1}
                max={selShopItem.isRandom && selShopItem.stock != null ? selShopItem.stock : undefined}
                value={qty}
                onChange={e => {
                  const max = selShopItem.isRandom && selShopItem.stock != null ? selShopItem.stock : 9999
                  setQty(Math.max(1, Math.min(max, Number(e.target.value))))
                }} />
            )}
            <span style={{ fontSize:12, color:'#a09060' }}>
              합계 {(selShopItem.buyPrice * qty).toLocaleString()}전
            </span>
            <button className="action-btn equip"
              disabled={busy || gs.char.gold < selShopItem.buyPrice * qty}
              onClick={doBuy}>구매</button>
          </div>
        </div>
      )}

      {/* 판매 액션 패널 */}
      {selInvItem && mode === 'sell' && (
        <div className="item-action-panel">
          <div className="item-action-title" style={{ color: GRADE_COLOR[selInvItem.grade] }}>
            {selInvItem.name}
          </div>
          <div className="item-action-info">
            {selInvItem.sellPrice > 0 && (
              <span style={{ color:'#e0c040' }}>개당 {selInvItem.sellPrice.toLocaleString()}전</span>
            )}
            <span>보유 {gs.char.gold.toLocaleString()}전</span>
          </div>
          <div className="item-action-btns">
            {selInvItem.qty > 1 && !['EQUIP','MUGONG_BOOK'].includes(selInvItem.category) && (
              <input type="number" className="drop-qty-input" min={1} max={selInvItem.qty} value={qty}
                onChange={e => setQty(Math.max(1, Math.min(selInvItem.qty, Number(e.target.value))))} />
            )}
            {selInvItem.sellPrice > 0 && selInvItem.qty > 1 && !['EQUIP','MUGONG_BOOK'].includes(selInvItem.category) && (
              <span style={{ fontSize:12, color:'#a09060' }}>
                합계 {(selInvItem.sellPrice * qty).toLocaleString()}전
              </span>
            )}
            <button className="action-btn drop" disabled={busy} onClick={doSell}>판매</button>
          </div>
        </div>
      )}
    </div>
  )
}
